import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Alert } from '../ui';
import { useSalon } from '../../contexts/SalonContext';
import { useAvailability } from '../../hooks/useAvailability';
import { formatarPreco } from '../../utils/priceUtils';
import { formatISO } from '../../utils/dateUtils';
import { db } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

const MobileDateTimeSelection = ({
  selectedServices,
  professionals,
  availableSlots,
  selectedDate,
  selectedTime,
  selectedProfessional,
  onDateSelect,
  onTimeSelect,
  onProfessionalSelect,
  onNext,
  onBack,
  loading: externalLoading,
  error: externalError,
  excludeAppointmentId
}) => {
  const { salonId } = useSalon();
  const [visibleDates, setVisibleDates] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [professionalCustomPrices, setProfessionalCustomPrices] = useState({});
  const [professionalLoadingState, setProfessionalLoadingState] = useState({});

  // Log para verificar se excludeAppointmentId está chegando corretamente
  useEffect(() => {
    if (excludeAppointmentId) {
      console.log(`[MobileDateTimeSelection] Usando excludeAppointmentId=${excludeAppointmentId} para a seleção de horários`);
    }
  }, [excludeAppointmentId]);

  // Formatação de data para comparação (YYYY-MM-DD) - Usando formatISO do dateUtils
  const formatDate = useCallback((date) => {
    return formatISO(date);
  }, []);

  // ADICIONADO: Normalizar os serviços selecionados para garantir que todos sejam strings (IDs)
  const normalizedSelectedServices = useMemo(() => {
    if (!selectedServices) return [];
    
    // Se selectedServices é um array de objetos, extrair apenas os IDs
    return selectedServices.map(service => 
      typeof service === 'object' && service.id ? service.id : service
    );
  }, [selectedServices]);
  
  // Registrar os serviços normalizados
  useEffect(() => {
    console.log('Serviços selecionados normalizados:', normalizedSelectedServices);
  }, [normalizedSelectedServices]);

  // Usar hook de disponibilidade
  const { 
    availabilityMap, 
    services,
    preloadAvailability, 
    getTimeSlotsForServices,
    isLoaded,
    isLoading
  } = useAvailability(salonId);
  
  // Gerar datas para o calendário (próximos 14 dias) - Convertido para useCallback para estabilizar
  const generateVisibleDates = useCallback(() => {
    const today = new Date();
    const dates = [];
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  }, []);
  
  // Inicializar datas visíveis
  useEffect(() => {
    const dates = generateVisibleDates();
    setVisibleDates(dates);
    
    // Pré-carregar disponibilidade para os próximos 7 dias (não todos os 14)
    // para reduzir o número de requisições iniciais
    const startDate = formatDate(dates[0]);
    const endDate = formatDate(dates[Math.min(7, dates.length - 1)]);
    
    // MODIFICADO: Sempre carregar as datas, sem verificações
    console.log(`[MobileDateTimeSelection] Pré-carregando disponibilidade inicial: ${startDate} a ${endDate}`);
    preloadAvailability(startDate, endDate);
    
    // ADICIONADO: Verificar e carregador dados de serviços
    console.log(`[MobileDateTimeSelection] Serviços selecionados:`, selectedServices);
    
  }, [generateVisibleDates, preloadAvailability, formatDate, selectedServices]);
  
  // Pré-carregar dados para o profissional quando for selecionado
  useEffect(() => {
    if (!selectedDate || !selectedProfessional || !salonId) return;
    
    const formattedDate = formatDate(selectedDate);
    const profId = selectedProfessional;
    
    // Verificar se já carregamos ou estamos carregando este profissional
    if (professionalLoadingState[profId]) return;
    
    // Se os dados para este profissional ainda não foram carregados E não está carregando
    if (!isLoaded(formattedDate, profId) && 
        !isLoading(formattedDate, formattedDate, profId)) {
      
      console.log(`[MobileDateTimeSelection] Carregando dados para profissional ${profId} na data ${formattedDate}`);
      
      // Marcar como carregando
      setProfessionalLoadingState(prev => ({
        ...prev,
        [profId]: 'loading'
      }));
      
      // Pré-carregar dados para este profissional
      setLocalLoading(true);
      
      preloadAvailability(formattedDate, formattedDate, profId)
        .then(() => {
          setLocalLoading(false);
          // Marcar como carregado
          setProfessionalLoadingState(prev => ({
            ...prev,
            [profId]: 'loaded'
          }));
        })
        .catch(error => {
          console.error('Erro ao carregar dados do profissional:', error);
          setLocalError('Erro ao carregar horários para este profissional.');
          setLocalLoading(false);
          // Marcar como erro
          setProfessionalLoadingState(prev => ({
            ...prev,
            [profId]: 'error'
          }));
        });
    }
  }, [selectedDate, selectedProfessional, isLoaded, preloadAvailability, isLoading, salonId, formatDate, professionalLoadingState]);
  
  // Formatar data para exibição
  const formatDateDisplay = useCallback((date) => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    return {
      dayOfWeek: days[date.getDay()],
      day: date.getDate(),
      month: months[date.getMonth()]
    };
  }, []);
  
  // Verificar se a data é hoje
  const isToday = useCallback((date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }, []);
  
  // Verificar se a data está selecionada
  const isDateSelected = useCallback((date) => {
    if (!selectedDate) return false;
    const formattedDate = formatDate(date);
    const formattedSelectedDate = typeof selectedDate === 'string' 
      ? selectedDate 
      : formatDate(selectedDate);
    return formattedDate === formattedSelectedDate;
  }, [selectedDate, formatDate]);
  
  // Manipulador de seleção de data
  const handleDateClick = useCallback((date) => {
    // Formatar data para ISO
    const formattedDate = formatDate(date);
    
    // Atualizar data selecionada
    onDateSelect(formattedDate);
    
    // Limpar horário ao mudar a data
    onTimeSelect(null);
    
    // Se um profissional estiver selecionado, pré-carregar disponibilidade
    // apenas se ainda não estiver carregado/carregando
    if (selectedProfessional && !isLoaded(formattedDate, selectedProfessional) && 
        !isLoading(formattedDate, formattedDate, selectedProfessional)) {
      
      console.log(`[MobileDateTimeSelection] Pré-carregando dados ao selecionar data ${formattedDate} para profissional ${selectedProfessional}`);
      preloadAvailability(formattedDate, formattedDate, selectedProfessional);
    }
  }, [formatDate, onDateSelect, onTimeSelect, selectedProfessional, isLoaded, isLoading, preloadAvailability]);
  
  // Manipulador de seleção de horário
  const handleTimeClick = useCallback((timeObj) => {
    // Todos os horários são clicáveis agora, independente da disponibilidade
    onTimeSelect(timeObj.time);
  }, [onTimeSelect]);
  
  // Manipulador de seleção de profissional - modificado para evitar loops
  const handleProfessionalSelect = useCallback((profId) => {
    // Importante: Certificar que não estamos no meio de outra operação
    if (localLoading) {
      return;
    }
    
    // MODIFICADO: Verificar se é o mesmo profissional já selecionado
    if (profId === selectedProfessional) {
      return; // Não fazer nada se é o mesmo profissional
    }
    
    // Atualizar o profissional selecionado
    onProfessionalSelect(profId);
    
    if (selectedDate) {
      // Pré-carregar disponibilidade para este profissional
      // apenas se ainda não estiver carregado/carregando
      const formattedDate = formatDate(selectedDate);
      
      // Usar nosso estado de carregamento local em vez de verificar novamente
      if (!professionalLoadingState[profId] && 
          !isLoaded(formattedDate, profId) && 
          !isLoading(formattedDate, formattedDate, profId)) {
        
        // Marcar como carregando para evitar chamadas repetidas
        setProfessionalLoadingState(prev => ({
          ...prev,
          [profId]: 'loading'
        }));
        
        preloadAvailability(formattedDate, formattedDate, profId)
          .then(() => {
            // Marcar como carregado
            setProfessionalLoadingState(prev => ({
              ...prev,
              [profId]: 'loaded'
            }));
          })
          .catch(error => {
            console.error('Erro ao carregar dados do profissional:', error);
            // Marcar como erro
            setProfessionalLoadingState(prev => ({
              ...prev,
              [profId]: 'error'
            }));
          });
      }
    }
  }, [localLoading, selectedProfessional, onProfessionalSelect, selectedDate, formatDate, professionalLoadingState, isLoaded, isLoading, preloadAvailability]);
  
  // Função para gerar slots de horário padrão
  const generateDefaultTimeSlots = useCallback(() => {
    const generatedTimes = [];
    for (let hour = 8; hour <= 19; hour++) {
      if (hour !== 13) { // Excluir 13h (hora de almoço)
        generatedTimes.push({
          time: `${hour.toString().padStart(2, '0')}:00`,
          available: true
        });
        if (hour < 19) {
          generatedTimes.push({
            time: `${hour.toString().padStart(2, '0')}:30`,
            available: true
          });
        }
      }
    }
    
    // Filtrar horários passados se for o dia atual
    if (selectedDate) {
      const today = new Date();
      const formattedSelectedDate = formatDate(selectedDate);
      const formattedToday = formatDate(today);
      
      if (formattedSelectedDate === formattedToday) {
        const currentHour = today.getHours();
        const currentMinute = today.getMinutes();
        
        return generatedTimes.filter(slot => {
          const [slotHour, slotMinute] = slot.time.split(':').map(Number);
          
          if (slotHour > currentHour) {
            return true;
          } else if (slotHour === currentHour) {
            return slotMinute > currentMinute;
          }
          
          return false;
        });
      }
    }
    
    return generatedTimes;
  }, [selectedDate, formatDate]);
  
  // Função para obter slots de horário disponíveis
  const getAvailableTimes = useCallback(() => {
    if (!selectedDate) return [];
    
    const formattedDate = formatDate(selectedDate);
    
    // ADICIONADO: Log para verificar os serviços selecionados
    console.log(`[getAvailableTimes] Data ${formattedDate}, Serviços:`, normalizedSelectedServices);
    
    // MODIFICADO: Retornar slots padrão para facilitar a seleção
    const defaultTimeSlots = generateDefaultTimeSlots();
    console.log(`[getAvailableTimes] Gerando ${defaultTimeSlots.length} slots padrão para a data ${formattedDate}`);
    
    // Se não há serviços selecionados ou não há profissional selecionado,
    // mostrar horários padrão para permitir seleção
    if (!normalizedSelectedServices || normalizedSelectedServices.length === 0 || !selectedProfessional) {
      return defaultTimeSlots;
    }
    
    // Se um profissional estiver selecionado, obter slots específicos
    if (selectedProfessional) {
      console.log(`[getAvailableTimes] Buscando slots para profissional ${selectedProfessional}`);
      
      const timeSlotsFromService = getTimeSlotsForServices(
        formattedDate,
        selectedProfessional,
        normalizedSelectedServices
      );
      
      console.log(`[getAvailableTimes] Slots encontrados para profissional:`, timeSlotsFromService);
      
      // MODIFICADO: Se não encontrou slots específicos, retornar slots padrão
      if (!timeSlotsFromService || timeSlotsFromService.length === 0) {
        console.log(`[getAvailableTimes] Sem slots específicos, usando padrão`);
        return defaultTimeSlots;
      }
      
      // Filtrar horários que já passaram se for o dia atual
      const today = new Date();
      const formattedToday = formatDate(today);
      
      if (formattedDate === formattedToday) {
        const currentHour = today.getHours();
        const currentMinute = today.getMinutes();
        
        return timeSlotsFromService.filter(slot => {
          const [slotHour, slotMinute] = slot.time.split(':').map(Number);
          
          if (slotHour > currentHour) {
            return true;
          } else if (slotHour === currentHour) {
            return slotMinute > currentMinute;
          }
          
          return false;
        });
      }
      
      return timeSlotsFromService;
    }
    
    // Caso contrário, retornar slots padrão
    return defaultTimeSlots;
  }, [selectedDate, formatDate, normalizedSelectedServices, selectedProfessional, getTimeSlotsForServices, generateDefaultTimeSlots]);
  
  // Obter slots de horário disponíveis
  const availableTimes = useMemo(() => getAvailableTimes(), [getAvailableTimes]);
  
  // Verificar status de disponibilidade para datas visíveis no calendário
  const getDateAvailabilityStatus = useCallback((date) => {
    const formattedDate = formatDate(date);
    
    // MODIFICADO: Log para depuração de cada data sendo processada
    console.log(`Verificando disponibilidade para data ${formattedDate}`);
    
    // Se não há serviços selecionados, todas as datas devem estar disponíveis
    if (!normalizedSelectedServices || normalizedSelectedServices.length === 0) {
      console.log(`Sem serviços selecionados para ${formattedDate}, considerando disponível`);
      return { available: true, hasData: false };
    }
    
    // MODIFICADO: Verificar se esta data existe nos dados de disponibilidade
    const dataExistsInAvailabilityMap = !!availabilityMap[formattedDate];
    console.log(`Data ${formattedDate} existe no mapa de disponibilidade: ${dataExistsInAvailabilityMap}`);
    
    // MODIFICADO: Se a data existe no mapa, considerar disponível independentemente dos profissionais
    if (dataExistsInAvailabilityMap) {
      const professionalsWithData = Object.keys(availabilityMap[formattedDate] || {});
      console.log(`Profissionais com dados para ${formattedDate}: ${professionalsWithData.length}`);
      
      // IMPORTANTE: Se temos qualquer dado para a data, considerá-la disponível
      if (professionalsWithData.length > 0) {
        return { 
          available: true, 
          hasData: true,
          availabilityRatio: 1.0 // Consideramos 100% disponível para simplificar
        };
      }
    }
    
    // Verificar se há dados para esta data (mantendo código original como fallback)
    const hasProfessionalsData = professionals.some(prof => 
      !prof.isCategory && 
      availabilityMap[formattedDate] && 
      availabilityMap[formattedDate][prof.id]
    );
    
    // Se não temos dados carregados, consideramos disponível por padrão
    if (!hasProfessionalsData) {
      console.log(`Sem dados de profissionais para ${formattedDate}, considerando disponível`);
      return { available: true, hasData: false };
    }
    
    let totalSlots = 0;
    let availableSlots = 0;
    
    // Para cada profissional, verificar quantos slots estão disponíveis
    professionals.filter(p => !p.isCategory).forEach(prof => {
      if (!availabilityMap[formattedDate] || !availabilityMap[formattedDate][prof.id]) return;
      
      const profSlots = availabilityMap[formattedDate][prof.id];
      
      // Se temos qualquer slot para o profissional, a data deve estar disponível
      if (profSlots && profSlots.length > 0) {
        // Contar os slots
        profSlots.forEach(slot => {
          totalSlots++;
          if (slot.available || slot.allowsOverlap) {
            availableSlots++;
          }
        });
      }
    });
    
    // Adicionar logs para debug
    console.log(`[getDateAvailabilityStatus] Data ${formattedDate}: totalSlots=${totalSlots}, availableSlots=${availableSlots}`);
    
    // MODIFICADO: Mesmo se não houver slots disponíveis, considerar a data disponível
    // se a data existe no mapa de disponibilidade
    return { 
      available: true, // MODIFICADO: Sempre true para permitir seleção
      hasData: totalSlots > 0,
      availabilityRatio: totalSlots > 0 ? availableSlots / totalSlots : 0
    };
  }, [availabilityMap, formatDate, professionals, normalizedSelectedServices]);
  
  // Função para filtrar profissionais disponíveis para o horário selecionado
  const getAvailableProfessionalsForTime = useCallback(() => {
    if (!selectedDate || !selectedTime) {
      console.log('[getAvailableProfessionalsForTime] Sem data ou horário selecionado');
      return professionals;
    }
    
    console.log(`[getAvailableProfessionalsForTime] Buscando profissionais para ${selectedDate} ${selectedTime}`);
    console.log(`[getAvailableProfessionalsForTime] Serviços selecionados:`, normalizedSelectedServices);
    
    const formattedDate = formatDate(selectedDate);
    
    // Se não há serviços selecionados, mostrar todos os profissionais
    if (!normalizedSelectedServices || normalizedSelectedServices.length === 0) {
      console.log('[getAvailableProfessionalsForTime] Sem serviços selecionados, retornando todos os profissionais');
      return professionals;
    }
    
    // Filtrar profissionais que estão disponíveis no horário selecionado
    console.log(`[getAvailableProfessionalsForTime] Filtrando ${professionals.length} profissionais`);
    
    return professionals.filter(prof => {
      if (prof.isCategory) return false;
      
      // Se não temos dados de disponibilidade para este profissional, considerá-lo disponível
      if (!availabilityMap[formattedDate] || !availabilityMap[formattedDate][prof.id]) {
        console.log(`[getAvailableProfessionalsForTime] Sem dados para profissional ${prof.id}, considerando disponível`);
        return true;
      }
      
      // Buscar slot de horário para este profissional
      const profSlots = availabilityMap[formattedDate][prof.id];
      const slot = profSlots.find(slot => slot.time === selectedTime);
      
      // Profissional está disponível se o slot não existe ou está disponível ou permite sobreposição
      const isAvailable = !slot || slot.available || slot.allowsOverlap;
      
      console.log(`[getAvailableProfessionalsForTime] Profissional ${prof.id}: ${isAvailable ? 'disponível' : 'indisponível'}`);
      
      return isAvailable;
    });
  }, [selectedDate, selectedTime, availabilityMap, professionals, formatDate, normalizedSelectedServices]);
  
  // Filtrar profissionais disponíveis para o horário selecionado - convertido para useMemo
  const availableProfessionals = useMemo(() => getAvailableProfessionalsForTime(), [getAvailableProfessionalsForTime]);
  
  // Verificar se o profissional selecionado está disponível
  useEffect(() => {
    if (!selectedDate || !selectedTime || !selectedProfessional) return;
    
    const formattedDate = formatDate(selectedDate);
    
    // Se não temos dados para este profissional, não fazemos nada
    if (!availabilityMap[formattedDate] || !availabilityMap[formattedDate][selectedProfessional]) {
      return;
    }
    
    // Verificar se o profissional está disponível para o horário selecionado
    const profSlots = availabilityMap[formattedDate][selectedProfessional];
    const slot = profSlots.find(slot => slot.time === selectedTime);
    
    // Se o slot não está disponível e não permite sobreposição, limpar seleção de profissional
    if (slot && !slot.available && !slot.allowsOverlap) {
      onProfessionalSelect(null);
    }
  }, [selectedDate, selectedTime, selectedProfessional, availabilityMap, onProfessionalSelect]);
  
  // Buscar preços personalizados para os profissionais disponíveis
  useEffect(() => {
    // Evitar buscar preços se não tiver os dados necessários
    if (!salonId || !normalizedSelectedServices || normalizedSelectedServices.length === 0 || !selectedDate || !selectedTime) {
      setProfessionalCustomPrices({});
      return;
    }
    
    // Obter lista de profissionais disponíveis dentro do useEffect para evitar loop
    const professionalsToCheck = professionals.filter(prof => {
      if (prof.isCategory) return false;
      
      const formattedDate = formatDate(selectedDate);
      
      // Se não temos dados de disponibilidade para este profissional, considerá-lo disponível
      if (!availabilityMap[formattedDate] || !availabilityMap[formattedDate][prof.id]) {
        return true;
      }
      
      // Buscar slot de horário para este profissional
      const profSlots = availabilityMap[formattedDate][prof.id];
      const slot = profSlots.find(slot => slot.time === selectedTime);
      
      // Se não encontrou o slot ou o slot está disponível ou permite sobreposição
      return !slot || slot.available || slot.allowsOverlap;
    });
    
    // Usar um flag para controlar se estamos já buscando dados
    let isMounted = true;
    const fetchCustomPrices = async () => {
      try {
        if (!isMounted) return;
        
        // Buscar preços customizados para cada profissional disponível
        const customPrices = {};
        
        for (const professional of professionalsToCheck) {
          if (!isMounted) return;
          
          // Consultar preços personalizados para este profissional e serviços selecionados
          const professionalServicesRef = collection(db, `salons/${salonId}/professional_services`);
          const q = query(
            professionalServicesRef,
            where('professionalId', '==', professional.id),
            where('isActive', '==', true)
          );
          
          const snapshot = await getDocs(q);
          const customServicePrices = {};
          let hasCustomPrice = false;
          
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (normalizedSelectedServices.includes(data.serviceId) && data.customPrice !== null) {
              customServicePrices[data.serviceId] = {
                price: data.customPrice,
                serviceName: data.serviceName
              };
              hasCustomPrice = true;
            }
          });
          
          if (hasCustomPrice) {
            customPrices[professional.id] = customServicePrices;
          }
        }
        
        if (isMounted) {
          setProfessionalCustomPrices(customPrices);
        }
      } catch (error) {
        console.error("Erro ao buscar preços personalizados:", error);
      }
    };
    
    fetchCustomPrices();
    
    // Cleanup para evitar memory leaks e atualizações de estado em componentes desmontados
    return () => {
      isMounted = false;
    };
  }, [salonId, normalizedSelectedServices, selectedDate, selectedTime, professionals, availabilityMap, formatDate]);
  
  // Verificar loading
  const isLoadingState = externalLoading || localLoading || 
    (selectedDate && selectedProfessional && isLoading(formatDate(selectedDate), formatDate(selectedDate), selectedProfessional));
  
  // Mostrar loading se estiver carregando
  if (isLoadingState) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-blue-500 border-r-transparent mb-4"></div>
          <p className="text-gray-600">Carregando horários disponíveis...</p>
        </div>
      </div>
    );
  }
  
  // Mostrar erro 
  const displayError = externalError || localError;
  if (displayError) {
    return <Alert type="error" title="Erro ao carregar horários">{displayError}</Alert>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Selecione a data e hora
      </h2>
      
      {/* Calendário horizontal */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Selecione a data</h3>
        <div className="flex overflow-x-auto py-2 -mx-4 px-4 space-x-2 hide-scrollbar">
          {visibleDates.map((date, index) => {
            const { dayOfWeek, day, month } = formatDateDisplay(date);
            const { available, hasData, availabilityRatio } = getDateAvailabilityStatus(date);
            
            // ADICIONADO: Logs de debug para entender o estado de cada data
            console.log(`Data ${formatDate(date)}: hasData=${hasData}, available=${available}, ratio=${availabilityRatio}`);
            
            // Determinar classe de disponibilidade com base na razão
            let availabilityClass = '';
            let availabilityText = '';
            
            if (hasData) {
              if (availabilityRatio > 0.7) {
                availabilityClass = 'bg-green-50';
                availabilityText = 'Boa disponibilidade';
              } else if (availabilityRatio > 0.3) {
                availabilityClass = 'bg-yellow-50';
                availabilityText = 'Média disponibilidade';
              } else if (availabilityRatio > 0) {
                availabilityClass = 'bg-orange-50';
                availabilityText = 'Pouca disponibilidade';
              } else {
                availabilityClass = 'bg-gray-100 text-gray-500';
                availabilityText = 'Sem horários';
              }
            }
            
            // MODIFICADO: Garantir que todas as datas sejam clicáveis
            return (
              <div
                key={index}
                onClick={() => handleDateClick(date)}
                className={`flex-shrink-0 w-16 h-20 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-colors relative ${
                  isDateSelected(date)
                    ? 'bg-blue-500 text-white'
                    : hasData && !available  // Este trecho não deve ser mais usado já que todas as datas estão available=true
                    ? 'bg-white border border-gray-200 text-gray-700' // MODIFICADO: Melhor estilo visual
                    : hasData 
                    ? `bg-white border border-gray-200 ${availabilityClass} hover:bg-gray-50`
                    : 'bg-white border border-gray-200 hover:bg-gray-50'
                } ${
                  isToday(date) && !isDateSelected(date) ? 'border-blue-400 border-2' : ''
                }`}
              >
                <div className="text-xs font-medium">{dayOfWeek}</div>
                <div className="text-lg font-bold">{day}</div>
                <div className="text-xs">{month}</div>
                {hasData && !available && !isDateSelected(date) && (
                  <div className="text-xs mt-1 text-orange-500">Disponível</div> // MODIFICADO: "Disponível" em vez de "Verificando"
                )}
                {hasData && available && !isDateSelected(date) && availabilityRatio < 0.5 && (
                  <div className="text-xs mt-1 text-green-500">Disponível</div> // MODIFICADO: Verde para indicar disponibilidade
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Seleção de horários */}
      {selectedDate && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Horários disponíveis
          </h3>
          
          {availableTimes.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {availableTimes.map(timeObj => (
                <button
                  key={timeObj.time}
                  onClick={() => handleTimeClick(timeObj)}
                  className={`py-2.5 text-center rounded-md ${
                    selectedTime === timeObj.time
                      ? 'bg-blue-500 text-white'
                      : timeObj.available
                        ? 'bg-white border border-gray-300 hover:bg-gray-50'
                        : timeObj.allowsOverlap
                          ? 'bg-yellow-50 border border-yellow-300 hover:bg-yellow-100'
                          : 'bg-gray-100 border border-gray-200 hover:bg-gray-50'
                  }`}
                  title={!timeObj.available && timeObj.hasConflicts 
                    ? `Conflito: ${timeObj.conflicts.map(c => `${c.client} às ${c.time}`).join(', ')}` 
                    : null}
                >
                  {timeObj.time}
                  {!timeObj.available && timeObj.allowsOverlap && (
                    <span className="block text-xs text-yellow-600 mt-1">Sobreposto</span>
                  )}
                  {!timeObj.available && !timeObj.allowsOverlap && (
                    <span className="block text-xs text-gray-500 mt-1">Ocupado</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-600">Não há horários com profissionais disponíveis para esta data.</p>
              <p className="text-sm text-gray-500 mt-1">Tente selecionar outra data.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Seleção de profissional */}
      {selectedDate && selectedTime && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Profissionais Disponíveis
          </h3>
          
          {availableProfessionals.length > 0 ? (
          <div className="space-y-2">
              {availableProfessionals.filter(p => !p.isCategory).map(professional => {
                // Verificar se o profissional tem preços personalizados
                const hasCustomPrices = professionalCustomPrices[professional.id];
                const customPricesArray = hasCustomPrices 
                  ? Object.entries(professionalCustomPrices[professional.id]).map(([serviceId, data]) => ({
                      serviceId,
                      serviceName: data.serviceName,
                      price: data.price
                    }))
                  : [];
                
                return (
                  <div 
                    key={professional.id}
                    onClick={() => handleProfessionalSelect(professional.id)}
                    className={`
                      p-3 rounded-lg border transition-all cursor-pointer
                      ${selectedProfessional === professional.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {professional.foto ? (
                        <img 
                          src={professional.foto} 
                          alt={professional.nome}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-lg font-medium text-gray-500">
                            {professional.nome ? professional.nome[0].toUpperCase() : '?'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{professional.nome}</h4>
                        <p className="text-xs text-gray-500">
                          {professional.especialidade || "Profissional"}
                        </p>
                        
                        {/* Exibir preços personalizados, se houver */}
                        {customPricesArray.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {customPricesArray.map((item, index) => (
                              <p key={index} className="text-xs text-blue-600">
                                {item.serviceName}: {formatarPreco(item.price)}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                        selectedProfessional === professional.id
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'border-gray-300'
                      }`}>
                        {selectedProfessional === professional.id ? '✓' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-600">Não há profissionais disponíveis para este horário.</p>
              <p className="text-sm text-gray-500 mt-1">Tente selecionar outro horário ou data.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Barra inferior fixa com botões */}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg border-t border-gray-200 z-10 flex justify-between">
        <Button 
          onClick={onBack} 
          variant="outline"
        >
          Voltar
        </Button>
        
        <Button 
          onClick={onNext} 
          variant="primary"
          disabled={!selectedDate || !selectedTime || !selectedProfessional}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

export default MobileDateTimeSelection; 